/**
 * Tools API
 * ტულების API
 */

import { get, post, put, del } from '@aiia/portal-core';

export type ToolAuthMode = 'INTERNAL' | 'API_KEY' | 'BEARER_TOKEN' | 'BASIC_AUTH' | 'NONE';

export interface ParameterDefinition {
  type: string;
  description: string;
}

export interface WebhookConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  timeoutMs: number;
  retryCount: number;
}

export interface UserTool {
  id: string;
  userId: string;
  toolName: string;
  toolDescription: string;
  toolCategory: string;
  enabled: boolean;
  requiresConfirmation: boolean;
  // Parameters
  parametersSchema?: Record<string, ParameterDefinition>;
  requiredParams?: string[];
  // Webhook
  webhookUrl?: string;
  webhookMethod?: string;
  webhookHeaders?: Record<string, string>;
  webhookTimeoutMs?: number;
  webhookRetryCount?: number;
  hasWebhookUrl: boolean;
  // Templates
  requestTemplate?: Record<string, unknown>;
  successMessageTemplate?: string;
  errorMessageTemplate?: string;
  responseMapping?: Record<string, string>;
  // Secrets (only keys, not values)
  secretKeys: string[];
}

export interface CreateToolRequest {
  toolName: string;
  toolDescription: string;
  toolCategory: string;
  parametersSchema?: Record<string, ParameterDefinition>;
  requiredParams?: string[];
  webhookUrl?: string;
  webhookMethod?: string;
  webhookHeaders?: Record<string, string>;
  webhookTimeoutMs?: number;
  webhookRetryCount?: number;
  requestTemplate?: Record<string, unknown>;
  successMessageTemplate?: string;
  errorMessageTemplate?: string;
  responseMapping?: Record<string, string>;
  enabled?: boolean;
  requiresConfirmation?: boolean;
  authMode?: ToolAuthMode;
  secrets?: Record<string, string>;
}

export interface UpdateToolRequest extends Partial<CreateToolRequest> {}

// Tool categories
export const TOOL_CATEGORIES = [
  { value: 'LEAD', label: 'Lead Generation' },
  { value: 'BOOKING', label: 'Booking' },
  { value: 'INQUIRY', label: 'Inquiry' },
  { value: 'CRM', label: 'CRM' },
  { value: 'HR', label: 'HR' },
  { value: 'CUSTOMER_SERVICE', label: 'Customer Service' },
  { value: 'DATA_RETRIEVAL', label: 'Data Retrieval' },
  { value: 'NOTIFICATION', label: 'Notification' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'SCHEDULING', label: 'Scheduling' },
  { value: 'CUSTOM', label: 'Custom' },
  { value: 'OTHER', label: 'Other' },
];

// Auth modes
export const TOOL_AUTH_MODES = [
  { value: 'INTERNAL', label: 'Internal (JWT Forwarding)', description: 'Forward user JWT to internal API' },
  { value: 'API_KEY', label: 'API Key', description: 'Use configured API key header' },
  { value: 'BEARER_TOKEN', label: 'Bearer Token', description: 'Use bearer token authentication' },
  { value: 'BASIC_AUTH', label: 'Basic Auth', description: 'Use basic authentication' },
  { value: 'NONE', label: 'None', description: 'No authentication required' },
];

// Get all tools
export const getTools = () =>
  get<UserTool[]>('/tools');

// Get tool by ID
export const getTool = (id: string) =>
  get<UserTool>(`/tools/${id}`);

// Create tool
export const createTool = (data: CreateToolRequest) =>
  post<UserTool>('/tools', data);

// Update tool
export const updateTool = (id: string, data: UpdateToolRequest) =>
  put<UserTool>(`/tools/${id}`, data);

// Enable tool
export const enableTool = (id: string) =>
  put<UserTool>(`/tools/${id}/enable`, {});

// Disable tool
export const disableTool = (id: string) =>
  put<UserTool>(`/tools/${id}/disable`, {});

// Update tool secrets
export const updateToolSecrets = (id: string, secrets: Record<string, string>) =>
  put<void>(`/tools/${id}/secrets`, secrets);

// Delete tool
export const deleteTool = (id: string) =>
  del<void>(`/tools/${id}`);

// Test tool execution
export const testTool = (id: string, parameters: Record<string, unknown>) =>
  post<{ success: boolean; message: string; executionTimeMs: number }>(`/tools/${id}/test`, parameters);
